import axios, { type AxiosInstance, isAxiosError } from "axios";
import { config } from "../config";
import { vgsPostUrl } from "../utils/tokenization-config";
import { getDeviceSession } from "../device/seon-session";
import type {
  IWidgetSession,
  ITermsBlock,
} from "../shared/session/interfaces/session";
import { LINK_ERROR_CODE, RequestStatus } from "../utils/commons";
import { resolveUrlLinkId } from "../utils/resolveUrlLinkId";
import { resolveXLinkIdHeader } from "./resolveXLinkIdHeader";
import type { FundingMethod, PayoutMethod } from "../utils/catalog";
import { useSessionStore } from "../store/useSessionStore";
import { useOfflineStore } from "../store/useOfflineStore";
import { isAxiosNetworkFailure } from "../utils/axiosNetworkError";

/**
 * Error class for link claim errors
 */
export class LinkClaimError extends Error {
  constructor(
    message: string,
    public code: LINK_ERROR_CODE,
    public statusCode?: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = "LinkClaimError";
  }
}

export interface WidgetApiError {
  code: string;
  message: string;
  details?: unknown;
}

/** Extracts the normalized `{ code, message }` payload from a widget API error. */
export const getWidgetApiError = (error: unknown): WidgetApiError | null => {
  if (!isAxiosError(error)) return null;
  const body = error.response?.data?.error;
  if (!body || typeof body.code !== "string") return null;
  return {
    code: body.code,
    message: typeof body.message === "string" ? body.message : "",
    details: body.details,
  };
};

/**
 * Codes that make the link permanently unusable: the widget cannot recover from
 * them and must show the matching explanation instead of a generic error.
 */
const DEAD_LINK_CODES: string[] = [
  LINK_ERROR_CODE.LINK_NOT_FOUND,
  LINK_ERROR_CODE.LINK_EXPIRED,
  LINK_ERROR_CODE.LINK_REVOKED,
];

/**
 * Resolves the claim/API failure into a link error code. The backend `code`
 * wins over the HTTP status: a revoked link answers 409 `LINK_REVOKED` on
 * claim and 403 `LINK_REVOKED` on the partner CIP ticket, so status alone
 * cannot tell "revoked" from "already used".
 */
const resolveLinkErrorCode = (
  status: number | undefined,
  apiCode: string | undefined
): LINK_ERROR_CODE => {
  if (apiCode && apiCode in LINK_ERROR_CODE) {
    return apiCode as LINK_ERROR_CODE;
  }

  switch (status) {
    case 400:
      return LINK_ERROR_CODE.INVALID_TOKEN;
    case 404:
      return LINK_ERROR_CODE.LINK_NOT_FOUND;
    case 409:
      return LINK_ERROR_CODE.LINK_ALREADY_USED;
    case 410:
      return LINK_ERROR_CODE.LINK_EXPIRED;
    case 429:
      return LINK_ERROR_CODE.RATE_LIMITED;
    default:
      // No status means the request never reached the backend (network/CORS).
      return status === undefined || status >= 500
        ? LINK_ERROR_CODE.SERVICE_UNAVAILABLE
        : LINK_ERROR_CODE.INTERNAL_ERROR;
  }
};

/**
 * Custom event for dead links (LINK_NOT_FOUND, LINK_EXPIRED, LINK_REVOKED).
 * Dispatched when the API returns these errors on any widget endpoint.
 */
export const SESSION_EXPIRED_EVENT = "raas-widgets-flow-m2m-session-expired";

export const MAINTENANCE_EVENT = "raas-widgets-flow-m2m-maintenance";

export interface MaintenanceStatus {
  active: boolean;
  title?: string;
  body?: string;
  supportEmail?: string;
  supportWhatsappNumber?: string;
}
export const USER_BLOCKED_EVENT = "raas-widgets-flow-m2m-user-blocked";

/**
 * True when the failure means the link itself died (expired / revoked / gone).
 * `useSessionExpiredHandler` already routes those to the explanatory screen, so
 * callers must not overwrite it with their own generic error copy.
 */
export const isDeadLinkError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  const code = error.response?.data?.error?.code;
  return DEAD_LINK_CODES.includes(code) || error.response?.status === 410;
};

/**
 * Current link ID for API requests
 * Set after successful claim via setWidgetLinkId()
 */
let currentLinkId: string | null = null;

/**
 * Set the current link ID for API requests
 * This adds the x-link-id header to all subsequent requests
 * @param linkId - The link ID to use, or null to clear
 */
export const setWidgetLinkId = (linkId: string | null) => {
  currentLinkId = linkId;
};

export const getWidgetLinkId = (): string | null => currentLinkId;

export const probeLinkSession = async (
  linkId: string
): Promise<{ blocked: boolean; expiresAt: string | null }> => {
  const { data } = await widgetApi.get(`/widget/links/${linkId}/session`, {
    raasSilentOffline: true,
  });
  const expiresAt = data?.data?.expiresAt;
  return {
    blocked: Boolean(
      data?.data?.userStatus?.blocked ?? data?.data?.user?.blocked
    ),
    expiresAt: typeof expiresAt === "string" ? expiresAt : null,
  };
};

export const fetchMaintenanceStatus = async (
  linkId: string,
  locale: string,
): Promise<MaintenanceStatus> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/maintenance`,
    { params: { locale }, raasSilentOffline: true },
  );
  const payload = data?.data;
  if (!payload?.active) return { active: false };
  return {
    active: true,
    title: typeof payload.title === "string" ? payload.title : undefined,
    body: typeof payload.body === "string" ? payload.body : undefined,
    supportEmail:
      typeof payload.supportEmail === "string" ? payload.supportEmail : undefined,
    supportWhatsappNumber:
      typeof payload.supportWhatsappNumber === "string"
        ? payload.supportWhatsappNumber
        : undefined,
  };
};

export const dispatchSessionExpiredEvent = (detail: {
  code: string;
  details?: unknown;
}): void => {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail }));
};

function extractRequestLinkId(req: { headers?: unknown; url?: string } | undefined): string | null {
  if (!req) return null;
  const headers = req.headers as Record<string, unknown> | undefined;
  const header = headers?.["x-link-id"] ?? headers?.["X-Link-Id"];
  if (typeof header === "string" && header.length > 0) return header;
  const match = (req.url ?? "").match(/\/widget\/links\/([^/?]+)/);
  return match?.[1] ?? null;
}

export function shouldBroadcastSessionExpired(req: {
  headers?: unknown;
  url?: string;
} | undefined): boolean {
  if (useSessionStore.getState().session.status === RequestStatus.LOADING) {
    return false;
  }
  const reqLinkId = extractRequestLinkId(req);
  const urlLinkId = resolveUrlLinkId();
  if (reqLinkId && urlLinkId && reqLinkId !== urlLinkId) {
    return false;
  }
  return true;
}

/** ngrok free returns an HTML interstitial unless this header is present. */
const isNgrokBaseUrl = (url: string): boolean =>
  /ngrok(-free)?\.(app|dev)/i.test(url);

const ngrokSkipHeaders = (
  baseUrl: string
): Record<string, string> | undefined =>
  isNgrokBaseUrl(baseUrl)
    ? { "ngrok-skip-browser-warning": "true" }
    : undefined;

/**
 * Create axios instance for widget API calls
 */
const createWidgetApiInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: config.API_BASE_URL,
  });

  // Request interceptor to add x-link-id header
  instance.interceptors.request.use((requestConfig) => {
    const skipHeaders = ngrokSkipHeaders(config.API_BASE_URL);
    if (skipHeaders) {
      Object.assign(requestConfig.headers, skipHeaders);
    }
    // Bypass a disk-cached 410 on header-only GETs (catalog URL has no linkId).
    const method = (requestConfig.method ?? "get").toLowerCase();
    if (method === "get") {
      requestConfig.headers["Cache-Control"] = "no-cache";
      requestConfig.headers["Pragma"] = "no-cache";
    }
    const urlLinkId = resolveUrlLinkId();
    const persisted =
      useSessionStore.getState().session.data?.linkId ?? null;
    if (urlLinkId && currentLinkId !== urlLinkId) {
      setWidgetLinkId(urlLinkId);
    }
    const header = resolveXLinkIdHeader({
      urlLinkId,
      currentLinkId,
      persistedLinkId: persisted,
    });
    if (header) {
      requestConfig.headers["x-link-id"] = header;
    }
    return requestConfig;
  });

  // Response interceptor to catch dead-link errors globally
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const errorCode = error?.response?.data?.error?.code;
      const statusCode = error?.response?.status;

      if (errorCode === "USER_BLOCKED") {
        window.dispatchEvent(new CustomEvent(USER_BLOCKED_EVENT));
      }

      if (errorCode === LINK_ERROR_CODE.SERVICE_UNAVAILABLE_MAINTENANCE) {
        window.dispatchEvent(
          new CustomEvent(MAINTENANCE_EVENT, {
            detail: error?.response?.data?.error?.details,
          }),
        );
      }

      if (
        (DEAD_LINK_CODES.includes(errorCode) || statusCode === 410) &&
        shouldBroadcastSessionExpired(error?.config)
      ) {
        window.dispatchEvent(
          new CustomEvent(SESSION_EXPIRED_EVENT, {
            detail: {
              code: errorCode || LINK_ERROR_CODE.LINK_EXPIRED,
              details: error?.response?.data?.error?.details,
            },
          })
        );
      }

      if (
        isAxiosNetworkFailure(error) &&
        error.config?.raasSilentOffline !== true
      ) {
        useOfflineStore.getState().reportOffline();
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// Create singleton instance for widget API
export const widgetApi = createWidgetApiInstance();

export interface NotificationPreferenceChannel {
  channel: "in_app" | "email" | "sms";
  enabled: boolean;
}

export interface SmsConsentEntry {
  id: string;
  introMarkdown: string;
  detailTitle?: string;
  detailMarkdown: string;
}

export interface SmsConsentData {
  available: boolean;
  version?: string;
  maskedPhone?: string;
  current: "accepted" | "declined" | "none";
  language?: string;
  entries: SmsConsentEntry[];
}

export const getSmsConsent = async (
  linkId: string,
  lang: string,
): Promise<SmsConsentData> => {
  const { data } = await widgetApi.get(`/widget/links/${linkId}/sms-consent`, {
    params: { lang },
  });
  return data.data;
};

/** Records Accept or Not now. Returns the refreshed preference map. */
export const decideSmsConsent = async (
  linkId: string,
  input: {
    decision: "accepted" | "declined";
    version: string;
    language: string;
  },
): Promise<NotificationPreferenceChannel[]> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/sms-consent`,
    input,
  );
  return data.data.channels;
};

export const getNotificationPreferences = async (
  linkId: string,
): Promise<NotificationPreferenceChannel[]> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/notification-preferences`,
  );
  return data.data.channels;
};

export const saveNotificationPreferences = async (
  linkId: string,
  channels: NotificationPreferenceChannel[],
): Promise<NotificationPreferenceChannel[]> => {
  const { data } = await widgetApi.put(
    `/widget/links/${linkId}/notification-preferences`,
    { channels },
  );
  return data.data.channels;
};

export const saveContactPhone = async (
  linkId: string,
  phone: string,
  locale: string
): Promise<{ hasPhoneNumber: boolean; phoneCountry?: string }> => {
  const { data } = await widgetApi.put(
    `/widget/links/${linkId}/contact/phone`,
    { phone, locale }
  );
  return data.data;
};


/**
 * Device info to send with claim request
 */
export interface IDeviceInfo {
  userAgent?: string;
  language?: string;
  timezone?: string;
}

/**
 * Claim a magic link by linkId
 * POST /widget/links/:linkId/claim
 */
export const claimLink = async (
  linkId: string,
  deviceInfo?: IDeviceInfo,
  seonSession?: string
): Promise<IWidgetSession> => {
  const apiBaseUrl = config.API_BASE_URL;

  try {
    const { data } = await axios.post(
      `${apiBaseUrl}/widget/links/${linkId}/claim`,
      {
        deviceInfo,
        ...(seonSession ? { seonSession } : {}),
      },
      { headers: ngrokSkipHeaders(apiBaseUrl) }
    );

    // Map backend response to IWidgetSession
    return {
      sessionId: data.data.sessionId,
      linkId: data.data.linkId,
      token: data.data.token,
      status: data.data.status,
      expiresAt: data.data.expiresAt,
      partner: {
        partnerId: data.data.partner.partnerId,
        name: data.data.partner.name,
        logoUrl: data.data.partner.logoUrl,
        primaryColor: data.data.partner.primaryColor,
        theme: data.data.partner.theme,
        supportEmail: data.data.partner.supportEmail,
        supportWhatsappNumber: data.data.partner.supportWhatsappNumber,
        sessionExpiredRedirectUrl: data.data.partner.sessionExpiredRedirectUrl,
      },
      userData: data.data.userData,
      user: data.data.user,
      userStatus: data.data.userStatus,
      config: data.data.config,
      language: data.data.language,
      flavor: data.data.flavor,
      themeMode: data.data.themeMode,
      termsAccepted: data.data.termsAccepted,
      termsAcceptedAt: data.data.termsAcceptedAt,
      terms: data.data.terms,
      ...(data.data.novu ? { novu: data.data.novu } : {}),
    };
  } catch (error) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    const apiError = getWidgetApiError(error);
    const code = resolveLinkErrorCode(status, apiError?.code);

    // The message is only a diagnostic fallback: the error screen resolves the
    // user-facing copy from `code` via i18n.
    throw new LinkClaimError(
      apiError?.message || `Failed to claim link (${code})`,
      code,
      status,
      apiError?.details,
    );
  }
};

/**
 * Get session for a linkId
 * GET /widget/links/:linkId/session
 */
export const getSession = async (linkId: string): Promise<IWidgetSession> => {
  const apiBaseUrl = config.API_BASE_URL;

  try {
    const { data } = await axios.get(
      `${apiBaseUrl}/widget/links/${linkId}/session`,
      { headers: ngrokSkipHeaders(apiBaseUrl) }
    );

    return {
      sessionId: data.data.sessionId,
      linkId: data.data.linkId,
      token: data.data.token,
      status: data.data.status,
      expiresAt: data.data.expiresAt,
      partner: {
        partnerId: data.data.partner.partnerId,
        name: data.data.partner.name,
        logoUrl: data.data.partner.logoUrl,
        primaryColor: data.data.partner.primaryColor,
        theme: data.data.partner.theme,
        sessionExpiredRedirectUrl: data.data.partner.sessionExpiredRedirectUrl,
      },
      userData: data.data.userData,
      user: data.data.user,
      userStatus: data.data.userStatus,
      config: data.data.config,
      progress: data.data.progress,
      termsAccepted: data.data.termsAccepted,
      termsAcceptedAt: data.data.termsAcceptedAt,
      terms: data.data.terms,
      ...(data.data.novu ? { novu: data.data.novu } : {}),
    };
  } catch (error: any) {
    if (error?.response?.status === 404) {
      throw new LinkClaimError(
        "Link not found",
        LINK_ERROR_CODE.LINK_NOT_FOUND,
        404
      );
    }

    if (error?.response?.status === 400) {
      const errorCode = error.response.data?.error?.code;
      throw new LinkClaimError(
        error.response.data?.error?.message || "Session not started",
        errorCode || LINK_ERROR_CODE.SESSION_NOT_STARTED,
        400
      );
    }

    if (error?.response?.status === 410) {
      throw new LinkClaimError(
        "Link expired",
        LINK_ERROR_CODE.LINK_EXPIRED,
        410,
        error?.response?.data?.error?.details,
      );
    }

    throw new LinkClaimError(
      "Failed to get session",
      LINK_ERROR_CODE.INTERNAL_ERROR,
      error?.response?.status
    );
  }
};

/**
 * Record the Terms & Conditions / E-Sign acceptance for the current user.
 * Persisted at the user level by the backend so the same user is not prompted
 * again on future links.
 * POST /widget/links/:linkId/accept-terms
 */
export interface IAcceptTermsResponse {
  termsAccepted: boolean;
  termsAcceptedAt?: string;
  termsAcceptedVersion?: string;
}

export const acceptTerms = async (
  linkId: string,
  options?: {
    version?: string;
    documents?: string[];
    acceptedEntryIds?: string[];
    language?: string;
  }
): Promise<IAcceptTermsResponse> => {
  const deviceInfo: IDeviceInfo = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/accept-terms`,
    {
      version: options?.version,
      documents: options?.documents,
      acceptedEntryIds: options?.acceptedEntryIds,
      language: options?.language,
      deviceInfo,
    }
  );

  return data.data;
};

// ============================================
// Operations API (Exchange Rate, Quote, Cards)
// ============================================

/**
 * Exchange rate response
 */
/**
 * Which side of the transfer the `amount` is pinned to. `send` keeps the
 * source amount fixed (the m2m widget collects the amount in source currency);
 * `receive` keeps the destination amount fixed.
 */
export type QuoteAnchor = "send" | "receive";

/**
 * Pre-quote grouping axis. `payer` returns one option per payer; `fundingMethod`
 * returns the cheapest option per funding method.
 */
export type PreQuoteVary = "payer" | "fundingMethod";

/**
 * Indicative pre-quote request. The widget provides the corridor + amount +
 * currencies and (optionally) the payout/funding method; the backend fans out
 * across every quotable capability and returns a ranked list of options.
 * POST /widget/pre-quote
 */
export interface IPreQuoteRequest {
  originCountryCode: string;
  destinationCountryCode: string;
  /** Interpreted per `anchor`: source ccy for `send`, destination for `receive`. */
  amount: number;
  /** Defaults to `send` server-side. */
  anchor?: QuoteAnchor;
  destinationCurrency: string;
  /** Defaults to `USD` server-side. */
  sourceCurrency?: string;
  fundingMethod?: string;
  payoutMethod?: string;
  /** Defaults to `payer` server-side. */
  vary?: PreQuoteVary;
}

/**
 * A single quotable capability (SKU) returned by the pre-quote, with its
 * indicative economics. `capabilityKey` is the binding identifier the widget
 * pins when requesting the signed quote.
 */
export interface IEstimateOption {
  capabilityKey: string;
  corridorCode: string;
  payerCode: string | null;
  payoutMethod: string;
  fundingMethod: string;
  sendAmount: number;
  receiveAmount: number;
  customerFee: number;
  customerFxRate: number;
  sourceCurrency: string;
  destinationCurrency: string;
}

export interface IEstimateRange {
  min: number;
  max: number;
  avg: number;
}

export interface IEstimateSummary {
  count: number;
  /** Cheapest option for the end user. Absent when `count === 0`. */
  best?: IEstimateOption;
  /** Most expensive offerable option. */
  worst?: IEstimateOption;
  rankingMetric: "receiveAmount" | "sendAmount";
  receiveAmount?: IEstimateRange;
  sendAmount?: IEstimateRange;
  asOf: string;
  indicative: true;
}

export interface IEstimateResult {
  options: IEstimateOption[];
  summary: IEstimateSummary;
}

/**
 * Get an indicative (non-binding) pre-quote across all quotable options.
 * POST /widget/pre-quote
 */
export const getPreQuote = async (
  input: IPreQuoteRequest
): Promise<IEstimateResult> => {
  const { data } = await widgetApi.post(`/widget/pre-quote`, input);
  return data.data;
};

/**
 * Quote request parameters
 */
export interface IQuoteRequest {
  sourceCountry: string;
  destinationCountry: string;
  /** Funding method (canonical catalog vocabulary, UPPER_SNAKE). */
  fundingMethod: FundingMethod;
  /** Payout method (canonical catalog vocabulary, UPPER_SNAKE). */
  payoutMethod: PayoutMethod;
  /**
   * Exact capability (SKU) chosen from the pre-quote options. The binding quote
   * is priced + frozen against this exact SKU by the pricing engine.
   */
  capabilityKey: string;
  /** Which leg `amount` is pinned to. Defaults to `send` server-side. */
  anchor?: QuoteAnchor;
  amount: number;
  /** Source-leg currency (ISO 4217). */
  currency: string;
  /** Destination currency; required when `anchor` is `receive`. */
  destinationCurrency?: string;
  cardId?: string; // Card ID for card payments (will be signed in the quote)
}

/**
 * Quote response
 */
export interface IQuoteResponse {
  quoteId: string;
  sendAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  /** @deprecated Use fundingFee + transactionFee + taxFee breakdown instead */
  fee?: number;
  totalFee?: number;
  totalCost: number;
  sourceCurrency: string;
  destinationCurrency: string;
  fundingMethod: FundingMethod;
  payoutMethod: PayoutMethod;
  expiresAt: string;
  signedQuote: string;
  cardId?: string;
  /**
   * Capability + anchor the quote was priced against. Not returned by the
   * backend; the widget stamps them locally so the QUOTE_EXPIRED refresh path
   * can re-request the same SKU.
   */
  capabilityKey?: string;
  anchor?: QuoteAnchor;
}

/**
 * Get a quote for a money transfer
 * POST /widget/operations/quote
 */
export const getQuote = async (
  request: IQuoteRequest,
  options?: { raasSilentOffline?: boolean },
): Promise<IQuoteResponse> => {
  const { data } = await widgetApi.post(
    `/widget/operations/quote`,
    request,
    { raasSilentOffline: options?.raasSilentOffline },
  );

  // Stamp the request's capability + anchor onto the response so downstream
  // consumers (e.g. QUOTE_EXPIRED refresh) can re-price the same SKU.
  return {
    ...data.data,
    capabilityKey: request.capabilityKey,
    anchor: request.anchor,
  };
};

/**
 * Stored card response
 */
export interface IStoredCard {
  cardId: string;
  last4: string;
  brand: string;
  expiryMonth: string;
  expiryYear: string;
  cardholderName?: string;
  isDefault: boolean;
}

/**
 * Add card request
 */
export interface IAddCardRequest {
  token: string; // VGS token
  last4: string;
  brand: string;
  expiryMonth: string;
  expiryYear: string;
  cardholderName?: string;
}

/**
 * Add a funding card for the user
 * POST /widget/links/:linkId/cards
 */
export const addCard = async (
  linkId: string,
  card: IAddCardRequest
): Promise<IStoredCard> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/cards`,
    card
  );

  return data.data;
};

/**
 * List all funding cards for the user
 * GET /widget/links/:linkId/cards
 */
export const listCards = async (linkId: string): Promise<IStoredCard[]> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/cards`
  );

  return data.data;
};

/**
 * Remove a funding card
 * DELETE /widget/links/:linkId/cards/:cardId
 */
export const removeCard = async (
  linkId: string,
  cardId: string
): Promise<void> => {
  await widgetApi.delete(`/widget/links/${linkId}/cards/${cardId}`);
};

// ============================================
// Operations Create API
// ============================================

/**
 * Create operation request.
 *
 * `fundingPaymentMethodId` is required for funding methods that consume a
 * stored instrument (DEBIT_CARD, CREDIT_CARD, ACH, RTP, BANK_BALANCE), and
 * omitted for CASH_DEPOSIT (paid in person; no stored instrument).
 */
export interface ICreateOperationRequest {
  signedQuote: string;
  fundingPaymentMethodId?: string;
  payoutPaymentMethodId?: string;
  notes?: string;
  /** Optional SEON Device Intelligence session captured at commit time. */
  seonSession?: string;
  /**
   * Recipient party. In the m2m flow the recipient IS the sender (self-send),
   * so the name comes from the claim's `userData` — building it here keeps
   * the business-case recipe in the flow (the backend persists the snapshot
   * verbatim and projects it for OFAC screening; it never infers identity).
   */
  recipient?: {
    name: {
      firstName: string;
      lastName: string;
      middleName?: string;
      secondLastName?: string;
    };
    address?: {
      countryISO2?: string;
      /** Full ISO 3166-2 subdivision code (e.g. `MX-JAL`). */
      state?: string;
      city?: string;
      line1?: string;
      zip?: string;
    };
    phone?: string;
  };
}

/**
 * Operation response
 */
export interface IOperationResponse {
  operationId: string;
  quoteId: string;
  status: string; // 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled'
  sendAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  /** @deprecated Use fundingFee + transactionFee + taxFee breakdown instead */
  fee?: number;
  fundingFee?: number;
  transactionFee?: number;
  taxFee?: number;
  totalFee?: number;
  totalCost: number;
  sourceCurrency: string;
  destinationCurrency: string;
  fundingPaymentMethodId?: string;
  payoutPaymentMethodId?: string;
  notes?: string;
  createdAt: string;
  cancellation?: {
    allowed: boolean;
    mode?: "void" | "request";
    expiresAt?: string;
    unavailableReason?: string;
    requestedAt?: string;
  };
}

/**
 * Create a money transfer operation
 * POST /widget/links/:linkId/operations
 */
export const createOperation = async (
  linkId: string,
  request: ICreateOperationRequest
): Promise<IOperationResponse> => {
  const seonSession =
    request.seonSession ?? (await getDeviceSession()) ?? undefined;
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/operations`,
    {
      ...request,
      ...(seonSession ? { seonSession } : {}),
    }
  );

  return data.data;
};

/**
 * List all operations for the user
 * GET /widget/links/:linkId/operations
 */
export const listOperations = async (
  linkId: string
): Promise<IOperationResponse[]> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/operations`
  );

  return data.data;
};

/**
 * Get a single operation by ID
 * GET /widget/links/:linkId/operations/:operationId
 */
export const getOperation = async (
  linkId: string,
  operationId: string,
  options?: { raasSilentOffline?: boolean },
): Promise<IOperationResponse> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/operations/${operationId}`,
    { raasSilentOffline: options?.raasSilentOffline },
  );

  return data.data;
};

/**
 * Cancel operation response
 */
export interface ICancelOperationResponse {
  operationId: string;
  status: string;
  cancellationReason: string;
  mode?: "void" | "request";
  corridorOutcome?: "ok" | "failed" | "not_applicable";
}

/**
 * Cancel a money transfer operation
 * POST /widget/links/:linkId/operations/:operationId/cancel
 */
export const cancelOperation = async (
  linkId: string,
  operationId: string
): Promise<ICancelOperationResponse> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/operations/${operationId}/cancel`
  );

  return data.data;
};

// ============================================
// Payment Methods API (new system with VGS proxy tokenization)
// ============================================

import type {
  ICardPaymentMethod,
  IAllPaymentMethods,
  IAddCardPaymentMethodRequest,
} from "../interfaces/payment-method";

type VgsAliasField = "card_number" | "card_cvc";

type VgsEchoBody = {
  json?: Partial<Record<VgsAliasField, string>>;
} & Partial<Record<VgsAliasField, string>>;

const pickAlias = (
  data: VgsEchoBody | null | undefined,
  field: VgsAliasField
): string | undefined => {
  const fromJson = data?.json?.[field];
  if (typeof fromJson === "string" && fromJson.length > 0) return fromJson;
  const top = data?.[field];
  if (typeof top === "string" && top.length > 0) return top;
  return undefined;
};

const isVgsAlias = (value: string | undefined): value is string =>
  typeof value === "string" && value.startsWith("tok_");

/**
 * Tokenize card data via the VGS inbound route.
 * POST https://{proxyHost}/post with { card_number, card_cvc }.
 */
export const tokenizeCard = async (
  pan: string,
  cvv: string
): Promise<{ numberToken: string; cvvToken: string }> => {
  const vgsProxyUrl = await vgsPostUrl();

  const { data } = await axios.post<VgsEchoBody>(
    vgsProxyUrl,
    { card_number: pan, card_cvc: cvv },
    { headers: { "Content-Type": "application/json" } }
  );

  const numberToken = pickAlias(data, "card_number");
  const cvvToken = pickAlias(data, "card_cvc");
  if (!isVgsAlias(numberToken) || !isVgsAlias(cvvToken)) {
    throw new Error("VGS_TOKENIZE_FAILED");
  }
  return { numberToken, cvvToken };
};

/**
 * Register a tokenized card as a payment method
 * POST /widget/links/:linkId/payment-methods/cards
 */
export const addCardPaymentMethod = async (
  linkId: string,
  card: IAddCardPaymentMethodRequest
): Promise<ICardPaymentMethod> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/payment-methods/cards`,
    card
  );
  return data.data;
};

/**
 * Remove a card payment method
 * DELETE /widget/links/:linkId/payment-methods/cards/:cardId
 */
export const removeCardPaymentMethod = async (
  linkId: string,
  cardId: string
): Promise<void> => {
  await widgetApi.delete(
    `/widget/links/${linkId}/payment-methods/cards/${cardId}`
  );
};

/**
 * Enable or disable a card payment method
 * PATCH /widget/links/:linkId/payment-methods/cards/:cardId
 */
export const updateCardPaymentMethod = async (
  linkId: string,
  cardId: string,
  enabled: boolean
): Promise<ICardPaymentMethod> => {
  const { data } = await widgetApi.patch(
    `/widget/links/${linkId}/payment-methods/cards/${cardId}`,
    { enabled }
  );
  return data.data;
};

/**
 * Get all payment methods for the user (cards, bank accounts, etc.)
 * GET /widget/links/:linkId/payment-methods
 */
export const getAllPaymentMethods = async (
  linkId: string
): Promise<IAllPaymentMethods> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/payment-methods`
  );
  return data.data;
};

// ============================================
// CIP Readiness API
// ============================================

/**
 * CIP readiness response
 */
export interface ICipReadinessResponse {
  shouldRunCip: boolean;
  reason: string;
  cipTicketUrl: string | null;
  cipTicketId: string | null;
  missingFields?: string[];
}

/**
 * Check if the user needs to complete CIP verification before transferring
 * GET /widget/links/:linkId/cip-readiness
 */
export const checkCipReadiness = async (
  linkId: string
): Promise<ICipReadinessResponse> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/cip-readiness`
  );

  return data.data;
};

export interface ILegalDocumentResponse {
  doc: string;
  lang: string;
  html: string;
  fetchedAt: string;
  fromCache: boolean;
}

/**
 * Fetch a legal document HTML via the backend proxy.
 * No x-link-id required — public endpoint.
 * GET /widget/legal/:doc/:lang
 */
export const getLegalDocument = async (
  doc: string,
  lang: string
): Promise<ILegalDocumentResponse> => {
  const { data } = await widgetApi.get(`/widget/legal/${doc}/${lang}`);
  return data.data;
};

/**
 * Fetch the partner Terms & Conditions for the link in a specific language.
 * The backend resolves auto-generated translations and falls back to English
 * when the requested language is unavailable.
 * GET /widget/links/:linkId/terms?lang=xx
 */
export const getTerms = async (
  linkId: string,
  lang?: string
): Promise<ITermsBlock | null> => {
  const { data } = await widgetApi.get(`/widget/links/${linkId}/terms`, {
    params: lang ? { lang } : undefined,
  });
  return data.data?.terms ?? null;
};
